/**
 * Operator data-access helpers (Phase I, T-048..T-056; T-068 server cycle).
 *
 * Thin supabase-js wrappers for the operator dashboard. No ORM (ARCH-001).
 * RLS via migration 0007's `*_operator_all` policies; the JWT app_metadata
 * claim `role=operator` (T-016) is what permits these reads. No service-role
 * key is used in-browser (TECH-08); the operator session reads its own data
 * through the same RLS surface.
 *
 * State-changing operator actions (the 10 ARCH-004 §6 operator actions
 * listed in `@studio/studio-mcp` `operator-actions/contract.ts`) go through
 * the typed POST endpoints at `/api/v1/operator/*`. The shared service
 * function on the server side is the single SQL these actions run; the MCP
 * tool adapter calls the same function. The graceful-degradation helpers
 * below preserve a direct supabase write as fallback for the SANDBOX case
 * where the endpoint isn't registered (404/405). With the endpoints landed
 * in the T-068 server cycle, the fallback should only fire if an
 * environment is misconfigured.
 *
 * These helpers never page through table-level full scans: every list query
 * caps at a sensible bound (50-200 rows) appropriate for a single operator's
 * dashboard. Larger archive views can land later if needed.
 */

import { supabase } from './supabase.js';

/**
 * Shared adapter for calling an operator-action endpoint. Carries the
 * operator's Bearer access token, parses the JSON response, and surfaces
 * a discriminated outcome:
 *   - `ok` when the endpoint returned 200 (the body is the response payload)
 *   - `absent` on 404/405 (use the direct-supabase fallback in sandbox)
 *   - `error` on any other non-200 (the endpoint exists and rejected the call)
 *
 * 401/403 from the endpoint surface as `error` so the caller can show a
 * meaningful message — the deployed operator should always have valid
 * Bearer credentials and the operator role.
 */
export type OperatorEndpointOutcome<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'absent' } // endpoint not registered (sandbox/dev only)
  | { kind: 'error'; status: number; message: string };

export async function callOperatorEndpoint<T = Record<string, unknown>>(
  path: string,
  body: unknown,
): Promise<OperatorEndpointOutcome<T>> {
  let token: string | undefined;
  try {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token;
  } catch {
    // session lookup failed — surface as an error so the caller knows.
    return { kind: 'error', status: 0, message: 'No active session.' };
  }
  if (!token) {
    return { kind: 'error', status: 401, message: 'No active session.' };
  }
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return {
      kind: 'error',
      status: 0,
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
  if (res.status === 404 || res.status === 405) {
    return { kind: 'absent' };
  }
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* leave null */
  }
  if (res.status === 200) {
    return { kind: 'ok', data: (parsed ?? {}) as T };
  }
  const message =
    (parsed && typeof parsed === 'object' && 'error' in parsed
      ? typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : (parsed as { error: { message?: string } }).error?.message
      : undefined) ?? `Request failed (${res.status}).`;
  return { kind: 'error', status: res.status, message };
}
import {
  JSONB_SCHEMA_VERSIONS,
  type ContextDocSectionsPayload,
  type DeliverableContent,
  type EmailFrequency,
  type SourceRow,
  type Subscriber,
  type TaskStatus,
  type TaskType,
  type Tier,
  type VideoProvider,
} from '@studio/api-schemas';

// ---- Tasks ---------------------------------------------------------------

const ACTIVE_TASK_STATUSES: TaskStatus[] = [
  'ready_to_pull',
  'pulled',
  'in_progress',
  'agent_draft',
  'eric_reviewing',
  'ready_to_deliver',
];

export interface TaskRow {
  id: string;
  subscriber_id: string;
  type: TaskType;
  status: TaskStatus;
  brief: string;
  intake_mode: 'form' | 'chat' | null;
  coaching_depth: 'recommend' | 'show_me' | 'build_it_with_me' | null;
  parent_task_id: string | null;
  order_in_backlog: number | null;
  pull_time: string | null;
  due_at: string | null;
  delivered_at: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperatorTaskRow extends TaskRow {
  subscriber: {
    id: string;
    email: string;
    tier: Tier;
    status: Subscriber['status'];
  } | null;
}

const TASK_COLS =
  'id, subscriber_id, type, status, brief, intake_mode, coaching_depth, parent_task_id, order_in_backlog, pull_time, due_at, delivered_at, approved_at, created_at, updated_at';

const OPERATOR_TASK_SELECT = `${TASK_COLS}, subscriber:subscribers!inner(id, email, tier, status)`;

/**
 * All in-flight tasks across every subscriber. Source for /eric/active.
 */
export async function listActiveTasks(): Promise<OperatorTaskRow[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select(OPERATOR_TASK_SELECT)
    .in('status', ACTIVE_TASK_STATUSES)
    .is('deleted_at', null)
    .order('due_at', { ascending: true, nullsFirst: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as OperatorTaskRow[];
}

/**
 * Tasks with due_at inside the next `hours` hours (default 24h). Used by the
 * Today view per PR-27.
 */
export async function listDueWithin(hours = 24): Promise<OperatorTaskRow[]> {
  const cutoff = new Date(Date.now() + hours * 3_600_000).toISOString();
  const { data, error } = await supabase
    .from('tasks')
    .select(OPERATOR_TASK_SELECT)
    .in('status', ACTIVE_TASK_STATUSES)
    .is('deleted_at', null)
    .not('due_at', 'is', null)
    .lte('due_at', cutoff)
    .order('due_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as OperatorTaskRow[];
}

/**
 * Counters for the Today header strip. Hits three independent indexed
 * queries; cheap on the scale this dashboard runs at (≤ low hundreds of
 * tasks). Single COUNT call style keeps row payload at zero.
 */
export interface OperatorTodayCounters {
  inFlight: number;
  dueToday: number;
  needsAction: number;
}

export async function fetchTodayCounters(): Promise<OperatorTodayCounters> {
  const cutoff = new Date(Date.now() + 24 * 3_600_000).toISOString();
  const [inFlightRes, dueTodayRes, needsActionRes] = await Promise.all([
    supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .in('status', ACTIVE_TASK_STATUSES)
      .is('deleted_at', null),
    supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .in('status', ACTIVE_TASK_STATUSES)
      .is('deleted_at', null)
      .not('due_at', 'is', null)
      .lte('due_at', cutoff),
    // Needs-action approximation: tasks awaiting Eric (eric_reviewing,
    // agent_draft, ready_to_deliver). Refined below in listAwaitingAction.
    supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .in('status', ['agent_draft', 'eric_reviewing', 'ready_to_deliver'])
      .is('deleted_at', null),
  ]);
  if (inFlightRes.error) throw inFlightRes.error;
  if (dueTodayRes.error) throw dueTodayRes.error;
  if (needsActionRes.error) throw needsActionRes.error;
  return {
    inFlight: inFlightRes.count ?? 0,
    dueToday: dueTodayRes.count ?? 0,
    needsAction: needsActionRes.count ?? 0,
  };
}

/**
 * Persistent queue of things only Eric can resolve. Includes:
 *   - active tasks blocked on operator action (agent_draft / eric_reviewing
 *     / ready_to_deliver)
 *   - tasks recently approved (so Eric sees the approval row, per T-049 AC)
 *
 * Independent of due_at — appears here regardless of cycle timing.
 */
export interface AwaitingActionRow {
  kind: 'task' | 'approval';
  task: OperatorTaskRow;
}

export async function listAwaitingAction(): Promise<AwaitingActionRow[]> {
  const [pendingRes, approvedRes] = await Promise.all([
    supabase
      .from('tasks')
      .select(OPERATOR_TASK_SELECT)
      .in('status', ['agent_draft', 'eric_reviewing', 'ready_to_deliver'])
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })
      .limit(50),
    // Approval rows: recently approved tasks that have not yet been cleared
    // by Eric pulling the next item. We list the 20 most recent.
    supabase
      .from('tasks')
      .select(OPERATOR_TASK_SELECT)
      .eq('status', 'approved')
      .is('deleted_at', null)
      .order('approved_at', { ascending: false })
      .limit(20),
  ]);
  if (pendingRes.error) throw pendingRes.error;
  if (approvedRes.error) throw approvedRes.error;

  const pending: AwaitingActionRow[] = (
    (pendingRes.data ?? []) as unknown as OperatorTaskRow[]
  ).map((t) => ({ kind: 'task', task: t }));
  const approvals: AwaitingActionRow[] = (
    (approvedRes.data ?? []) as unknown as OperatorTaskRow[]
  ).map((t) => ({ kind: 'approval', task: t }));
  return [...approvals, ...pending];
}

/**
 * Content queue (PR-30). V1 reuses the task type taxonomy: "content" rows are
 * any tasks whose type indicates a content/copy deliverable. The taxonomy
 * itself is in api-schemas/enums.ts; we keep the operator-side filter inline
 * to avoid leaking content/non-content classification to a frozen module.
 */
const CONTENT_TASK_TYPES: TaskType[] = [
  'messaging_architecture',
  'voice_encoding',
  'pitch_deck_review',
];

export async function listContentQueue(): Promise<OperatorTaskRow[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select(OPERATOR_TASK_SELECT)
    .in('type', CONTENT_TASK_TYPES)
    .in('status', ACTIVE_TASK_STATUSES)
    .is('deleted_at', null)
    .order('due_at', { ascending: true, nullsFirst: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as OperatorTaskRow[];
}

// ---- Subscribers --------------------------------------------------------

export interface ClientRow {
  id: string;
  email: string;
  tier: Tier;
  status: Subscriber['status'];
  paused_at: string | null;
  paused_until: string | null;
  canceled_at: string | null;
  email_frequency: EmailFrequency;
  created_at: string;
  last_delivery_at: string | null;
  active_task_count: number;
}

/**
 * Clients table source (PR-31). Pulls the subscribers list, then joins last
 * delivery timestamp + active task count per row via a single tasks query
 * grouped client-side. Capped at 100 subscribers — well above expected V1
 * volume.
 */
export async function listClients(): Promise<ClientRow[]> {
  const { data: subscribers, error: sErr } = await supabase
    .from('subscribers')
    .select(
      'id, email, tier, status, paused_at, paused_until, canceled_at, email_frequency, created_at',
    )
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(100);
  if (sErr) throw sErr;

  const ids = (subscribers ?? []).map((s) => (s as { id: string }).id);
  if (ids.length === 0) return [];

  const { data: tasks, error: tErr } = await supabase
    .from('tasks')
    .select('subscriber_id, status, delivered_at')
    .in('subscriber_id', ids)
    .is('deleted_at', null);
  if (tErr) throw tErr;

  const lastDelivery = new Map<string, string>();
  const activeCount = new Map<string, number>();
  for (const t of (tasks ?? []) as Array<{
    subscriber_id: string;
    status: TaskStatus;
    delivered_at: string | null;
  }>) {
    if (
      t.delivered_at &&
      (!lastDelivery.has(t.subscriber_id) ||
        new Date(t.delivered_at) > new Date(lastDelivery.get(t.subscriber_id)!))
    ) {
      lastDelivery.set(t.subscriber_id, t.delivered_at);
    }
    if (ACTIVE_TASK_STATUSES.includes(t.status)) {
      activeCount.set(
        t.subscriber_id,
        (activeCount.get(t.subscriber_id) ?? 0) + 1,
      );
    }
  }

  return (subscribers ?? []).map((s) => {
    const row = s as {
      id: string;
      email: string;
      tier: Tier;
      status: Subscriber['status'];
      paused_at: string | null;
      paused_until: string | null;
      canceled_at: string | null;
      email_frequency: EmailFrequency;
      created_at: string;
    };
    return {
      ...row,
      last_delivery_at: lastDelivery.get(row.id) ?? null,
      active_task_count: activeCount.get(row.id) ?? 0,
    };
  });
}

// ---- Working View (T-052) -----------------------------------------------

export type MetaNoteSectionKey =
  | 'skeleton'
  | 'voice_decisions'
  | 'what_left_out'
  | 'do_yourself_next_time';

export type MetaNoteSectionState =
  | 'not_started'
  | 'in_progress'
  | 'draft_done'
  | 'ready_to_send';

export interface MetaNoteRow {
  id: string;
  task_id: string;
  skeleton: string | null;
  voice_decisions: string | null;
  what_left_out: string | null;
  do_yourself_next_time: string | null;
  skeleton_state: MetaNoteSectionState;
  voice_decisions_state: MetaNoteSectionState;
  what_left_out_state: MetaNoteSectionState;
  do_yourself_next_time_state: MetaNoteSectionState;
  sources_line: string | null;
  working_notes: string | null;
  working_notes_saved_at: string | null;
}

export interface WorkingTaskBundle {
  task: TaskRow;
  subscriber: {
    id: string;
    email: string;
    tier: Tier;
    status: Subscriber['status'];
  };
  contextSections: ContextDocSectionsPayload | null;
  metaNote: MetaNoteRow | null;
  sources: SourceRow[];
}

const META_NOTE_COLS =
  'id, task_id, skeleton, voice_decisions, what_left_out, do_yourself_next_time, skeleton_state, voice_decisions_state, what_left_out_state, do_yourself_next_time_state, sources_line, working_notes, working_notes_saved_at';

/**
 * Load every piece of data the Working View needs in one round trip per
 * resource: task + subscriber, context doc, meta note, sources. The meta
 * note may not exist yet (operator writes it during the cycle); if so,
 * createOrFetchMetaNote() seeds an empty row.
 */
export async function fetchWorkingTask(
  taskId: string,
): Promise<WorkingTaskBundle | null> {
  const { data: task, error: tErr } = await supabase
    .from('tasks')
    .select(
      `${TASK_COLS}, subscriber:subscribers!inner(id, email, tier, status)`,
    )
    .eq('id', taskId)
    .is('deleted_at', null)
    .maybeSingle();
  if (tErr) throw tErr;
  if (!task) return null;
  const row = task as unknown as TaskRow & {
    subscriber: {
      id: string;
      email: string;
      tier: Tier;
      status: Subscriber['status'];
    };
  };

  const [contextRes, metaRes, sourcesRes] = await Promise.all([
    supabase
      .from('context_docs')
      .select('sections')
      .eq('subscriber_id', row.subscriber.id)
      .is('deleted_at', null)
      .maybeSingle(),
    supabase
      .from('meta_notes')
      .select(META_NOTE_COLS)
      .eq('task_id', taskId)
      .maybeSingle(),
    supabase
      .from('sources')
      .select('id, task_id, type, ref, label, created_at')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true }),
  ]);
  if (contextRes.error) throw contextRes.error;
  if (metaRes.error) throw metaRes.error;
  if (sourcesRes.error) throw sourcesRes.error;

  return {
    task: row,
    subscriber: row.subscriber,
    contextSections: (contextRes.data?.sections ??
      null) as ContextDocSectionsPayload | null,
    metaNote: (metaRes.data ?? null) as MetaNoteRow | null,
    sources: ((sourcesRes.data ?? []) as SourceRow[]) ?? [],
  };
}

export interface DeliverableForOperator {
  task_id: string;
  content: DeliverableContent;
  content_schema_version: number;
  video_url: string | null;
  video_provider: VideoProvider | null;
}

/**
 * Read the deliverable row for an in-flight task. Pre-delivery the
 * `public_deliverables` view (migration 0008) is not yet populated (it filters
 * to status in delivered/approved), so the staging preview reads through the
 * base table under operator RLS (`deliverables_operator_all`, migration 0007).
 * Returns null when the agent has not yet written a deliverable for this task.
 */
export async function fetchDeliverableForOperator(
  taskId: string,
): Promise<DeliverableForOperator | null> {
  const { data, error } = await supabase
    .from('deliverables')
    .select('task_id, content, content_schema_version, video_url, video_provider')
    .eq('task_id', taskId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return data as unknown as DeliverableForOperator;
}

/**
 * Idempotently create the meta_notes row for a task. Returns the row whether
 * it was newly created or already existed.
 */
export async function ensureMetaNote(taskId: string): Promise<MetaNoteRow> {
  // Try insert first; on duplicate, fetch.
  const { data: inserted, error: insErr } = await supabase
    .from('meta_notes')
    .insert({ task_id: taskId })
    .select(META_NOTE_COLS)
    .single();
  if (!insErr && inserted) return inserted as unknown as MetaNoteRow;
  // Duplicate or RLS denial — fetch the existing.
  const { data, error } = await supabase
    .from('meta_notes')
    .select(META_NOTE_COLS)
    .eq('task_id', taskId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw insErr ?? new Error('meta_notes row could not be created.');
  return data as unknown as MetaNoteRow;
}

export interface MetaNoteSectionUpdate {
  section: MetaNoteSectionKey;
  body?: string;
  state?: MetaNoteSectionState;
}

const SECTION_BODY_COL: Record<MetaNoteSectionKey, string> = {
  skeleton: 'skeleton',
  voice_decisions: 'voice_decisions',
  what_left_out: 'what_left_out',
  do_yourself_next_time: 'do_yourself_next_time',
};

const SECTION_STATE_COL: Record<MetaNoteSectionKey, string> = {
  skeleton: 'skeleton_state',
  voice_decisions: 'voice_decisions_state',
  what_left_out: 'what_left_out_state',
  do_yourself_next_time: 'do_yourself_next_time_state',
};

/**
 * Update one or both of a meta_note section's body and state pill. The row
 * is identified by task_id (the meta_notes row has a unique constraint).
 */
export async function updateMetaNoteSection(
  taskId: string,
  update: MetaNoteSectionUpdate,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (update.body !== undefined) {
    patch[SECTION_BODY_COL[update.section]] = update.body;
  }
  if (update.state !== undefined) {
    patch[SECTION_STATE_COL[update.section]] = update.state;
  }
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase
    .from('meta_notes')
    .update(patch)
    .eq('task_id', taskId);
  if (error) throw error;
}

/**
 * Update the operator scratchpad (working_notes) and stamp working_notes_saved_at.
 */
export async function updateWorkingNotes(
  taskId: string,
  body: string,
): Promise<string> {
  const ts = new Date().toISOString();
  const { error } = await supabase
    .from('meta_notes')
    .update({ working_notes: body, working_notes_saved_at: ts })
    .eq('task_id', taskId);
  if (error) throw error;
  return ts;
}

/**
 * Update the sources_line (rendered "Sources: ..." line) on a meta note.
 */
export async function updateSourcesLine(
  taskId: string,
  body: string,
): Promise<void> {
  const { error } = await supabase
    .from('meta_notes')
    .update({ sources_line: body })
    .eq('task_id', taskId);
  if (error) throw error;
}

/**
 * Set task.status. Operator RLS permits any forward transition; the API
 * surface keeps this loose because the Working View's action bar gates
 * specific transitions in UI (e.g., Deliver gated to ready_to_deliver).
 */
export async function setTaskStatus(
  taskId: string,
  status: TaskStatus,
  extraPatch: Record<string, unknown> = {},
): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({ status, ...extraPatch })
    .eq('id', taskId);
  if (error) throw error;
}

/**
 * Split a task into N children (PR-39 / T-054). Creates the children with
 * status='backlog', sequential order_in_backlog values, parent_task_id
 * pointing at the parent, then flips the parent to status='split'. The
 * RLS surface permits the operator path; no SECURITY DEFINER RPC is
 * required because all writes are on tasks (operator_all). Returns the
 * created child IDs.
 */
export async function splitTask(
  parentTaskId: string,
  parent: { subscriber_id: string; type: TaskType },
  childBriefs: string[],
  startingOrder = 1000,
): Promise<string[]> {
  if (childBriefs.length < 2 || childBriefs.length > 4) {
    throw new Error('Split requires 2 to 4 child briefs.');
  }
  const insertRows = childBriefs.map((brief, i) => ({
    subscriber_id: parent.subscriber_id,
    parent_task_id: parentTaskId,
    type: parent.type,
    status: 'backlog' as TaskStatus,
    brief,
    order_in_backlog: startingOrder + i,
  }));
  const { data, error } = await supabase
    .from('tasks')
    .insert(insertRows)
    .select('id');
  if (error) throw error;
  const childIds = ((data ?? []) as Array<{ id: string }>).map((r) => r.id);

  const { error: parentErr } = await supabase
    .from('tasks')
    .update({ status: 'split' })
    .eq('id', parentTaskId);
  if (parentErr) throw parentErr;

  return childIds;
}

// ---- Resume candidates (T-056) ------------------------------------------

export interface ResumeCandidate {
  id: string;
  email: string;
  status: 'active' | 'paused' | 'canceled';
  paused_at: string | null;
}

/**
 * Subscribers who resumed after a >30 day pause and have not been
 * resume-acknowledged yet. The acknowledgment side is local-only in V1 (see
 * ReconHealthModal); this query just narrows the candidate pool.
 */
export async function listResumeCandidates(): Promise<ResumeCandidate[]> {
  const cutoff = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const { data, error } = await supabase
    .from('subscribers')
    .select('id, email, status, paused_at')
    .eq('status', 'active')
    .not('paused_at', 'is', null)
    .lte('paused_at', cutoff)
    .is('deleted_at', null)
    .limit(20);
  if (error) throw error;
  return (data ?? []) as ResumeCandidate[];
}

// ---- Waitlist + Contact --------------------------------------------------

export interface WaitlistRow {
  id: string;
  email: string;
  tier: Tier;
  note: string | null;
  notified_at: string | null;
  created_at: string;
}

export async function listWaitlist(): Promise<WaitlistRow[]> {
  const { data, error } = await supabase
    .from('waitlist')
    .select('id, email, tier, note, notified_at, created_at')
    .order('notified_at', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as WaitlistRow[];
}

export async function markWaitlistNotified(id: string): Promise<void> {
  const { error } = await supabase
    .from('waitlist')
    .update({ notified_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface ContactRow {
  id: string;
  name: string;
  email: string;
  company: string | null;
  situation: string;
  replied_at: string | null;
  created_at: string;
}

export async function listContactRequests(): Promise<ContactRow[]> {
  const { data, error } = await supabase
    .from('contact_requests')
    .select('id, name, email, company, situation, replied_at, created_at')
    .order('replied_at', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ContactRow[];
}

export async function markContactReplied(id: string): Promise<void> {
  const { error } = await supabase
    .from('contact_requests')
    .update({ replied_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ---- Capacity caps -------------------------------------------------------

export interface CapacityCapRow {
  tier: Tier;
  cap: number;
  updated_at: string;
}

export async function listCapacityCaps(): Promise<CapacityCapRow[]> {
  const { data, error } = await supabase
    .from('capacity_caps')
    .select('tier, cap, updated_at')
    .order('tier', { ascending: true });
  if (error) throw error;
  return (data ?? []) as CapacityCapRow[];
}

export async function upsertCapacityCap(
  tier: Tier,
  cap: number,
): Promise<void> {
  if (!Number.isInteger(cap) || cap < 0) {
    throw new Error('Cap must be a non-negative integer.');
  }
  const { error } = await supabase
    .from('capacity_caps')
    .upsert({ tier, cap }, { onConflict: 'tier' });
  if (error) throw error;
}

/**
 * Update a capacity cap via the operator-action endpoint. Endpoint-preferred
 * with a sandbox-only direct-supabase fallback. This is the same write the
 * Studio MCP `update_capacity` tool runs (ARCH-004 §6 single source).
 */
export async function updateCapacityViaEndpoint(
  tier: Tier,
  cap: number,
): Promise<void> {
  if (!Number.isInteger(cap) || cap < 0) {
    throw new Error('Cap must be a non-negative integer.');
  }
  const out = await callOperatorEndpoint('/api/v1/operator/capacity/update', {
    tier,
    cap,
  });
  if (out.kind === 'ok') return;
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  await upsertCapacityCap(tier, cap);
}

// ---- Agent telemetry (T-083, AI-14, TECH-22) ----------------------------

/**
 * Append-only telemetry row written by Studio MCP write tools (TECH-22).
 * Operator-only-RLS (migration 0007 `agent_invocations_operator_all`); a
 * subscriber JWT cannot SELECT. The operator dashboard reads the last N rows
 * for the per-subscriber + per-agent-type aggregates and the rolling P95
 * sparkline (AI-14). Shape mirrors migration 0005's `agent_invocations`
 * table, plus a flattened subscriber email pulled via the FK.
 */
export interface AgentInvocationRow {
  id: string;
  task_id: string | null;
  subscriber_id: string | null;
  agent_type: string;
  mcp_tool: string;
  frameworks_consulted: string[];
  sources_cited: string[];
  status: 'success' | 'error';
  latency_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  error_message: string | null;
  created_at: string;
  subscriber: { id: string; email: string } | null;
}

const AGENT_INVOCATIONS_COLS =
  'id, task_id, subscriber_id, agent_type, mcp_tool, frameworks_consulted, sources_cited, status, latency_ms, tokens_in, tokens_out, error_message, created_at, subscriber:subscribers(id, email)';

/**
 * Last `limit` agent invocations across every subscriber. Capped at 500 for
 * sanity (the dashboard does its own aggregation on the returned rows; the
 * underlying table is append-only so we keep the page small). Newest first.
 */
export async function listAgentInvocations(
  limit = 200,
): Promise<AgentInvocationRow[]> {
  const cap = Math.max(1, Math.min(500, limit));
  const { data, error } = await supabase
    .from('agent_invocations')
    .select(AGENT_INVOCATIONS_COLS)
    .order('created_at', { ascending: false })
    .limit(cap);
  if (error) throw error;
  return (data ?? []) as unknown as AgentInvocationRow[];
}

// ---- Progression reports (T-081, PR-21, PR-22) --------------------------

/**
 * One row in the /eric/reports queue. We carry just enough for the queue
 * row to render: subscriber email + tier, the period, generated_at (null
 * while the agent has not yet drafted), reviewed/delivered timestamps, and
 * the variant if the content has been drafted. `content` is the full
 * structured jsonb so the detail view can read it without a second query.
 */
export interface ProgressionReportRow {
  id: string;
  subscriber_id: string;
  period_start: string;
  period_end: string;
  content: Record<string, unknown> | null;
  content_schema_version: number;
  generated_at: string | null;
  reviewed_by_eric_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
  subscriber: { id: string; email: string; tier: Tier } | null;
}

const PROGRESSION_REPORT_COLS =
  'id, subscriber_id, period_start, period_end, content, content_schema_version, generated_at, reviewed_by_eric_at, delivered_at, created_at, updated_at, subscriber:subscribers!inner(id, email, tier)';

/**
 * Drafts in the operator review queue: any progression_reports row that is
 * not yet delivered. Includes rows where the agent has not run yet
 * (generated_at IS NULL) so Eric can see what is queued. Capped at 100;
 * Phase N volume is at most one per active Pro subscriber per month.
 */
export async function listProgressionReportDrafts(): Promise<ProgressionReportRow[]> {
  const { data, error } = await supabase
    .from('progression_reports')
    .select(PROGRESSION_REPORT_COLS)
    .is('delivered_at', null)
    .is('deleted_at', null)
    .order('generated_at', { ascending: false, nullsFirst: false })
    .order('period_start', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as ProgressionReportRow[];
}

/** Single report read for the detail view. */
export async function getProgressionReport(
  id: string,
): Promise<ProgressionReportRow | null> {
  const { data, error } = await supabase
    .from('progression_reports')
    .select(PROGRESSION_REPORT_COLS)
    .eq('id', id)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as ProgressionReportRow | null;
}

/**
 * Save edits Eric made to the report body before review. Writes the full
 * content jsonb in place; the operator RLS policy permits the update.
 */
export async function updateProgressionReportContent(
  id: string,
  content: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('progression_reports')
    .update({ content })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Mark a draft as reviewed (AI-06 gate). Setting reviewed_by_eric_at unlocks
 * the Send action. Idempotent: calling it twice keeps the first timestamp.
 */
export async function markProgressionReportReviewed(id: string): Promise<string> {
  const ts = new Date().toISOString();
  const { error } = await supabase
    .from('progression_reports')
    .update({ reviewed_by_eric_at: ts })
    .eq('id', id)
    .is('reviewed_by_eric_at', null);
  if (error) throw error;
  return ts;
}

// ---- Operator-action endpoint helpers (T-068 server cycle) --------------
//
// Each helper hits the matching /api/v1/operator/* endpoint registered in
// apps/portal/server/index.ts. The endpoint calls the shared service
// function in @studio/studio-mcp `operator-actions/services.ts`, so any
// invariant the MCP tool enforces (the AI-06 precondition reads, the
// notification-row insert that the T-047 dispatcher consumes, etc.) holds
// here too. Each helper is endpoint-preferred with graceful-degradation to
// a direct supabase write — kept as a sandbox safety net per the
// 2026-05-24 Eric call; with the endpoints registered the fallback should
// only fire if a deployment is misconfigured.

/** Pull a backlog/ready task into active work. */
export async function pullTaskById(taskId: string): Promise<void> {
  const out = await callOperatorEndpoint('/api/v1/operator/tasks/pull', {
    task_id: taskId,
  });
  if (out.kind === 'ok') return;
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only): operator RLS permits the direct update.
  const { error } = await supabase
    .from('tasks')
    .update({ status: 'pulled' })
    .eq('id', taskId);
  if (error) throw error;
}

/** Mark an agent_draft task as under Eric review (agent_draft -> eric_reviewing). */
export async function markAgentReviewedById(taskId: string): Promise<void> {
  const out = await callOperatorEndpoint(
    '/api/v1/operator/tasks/mark-agent-reviewed',
    { task_id: taskId },
  );
  if (out.kind === 'ok') return;
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  const { error } = await supabase
    .from('tasks')
    .update({ status: 'eric_reviewing' })
    .eq('id', taskId);
  if (error) throw error;
}

/** Promote a "Your notes" entry into a context-doc sub-field (stamps provenance). */
export async function promoteYourNote(
  noteId: string,
  subscriberId: string,
  section: string,
): Promise<{ promoted_to_context_at: string }> {
  const out = await callOperatorEndpoint<{ promoted_to_context_at: string }>(
    '/api/v1/operator/your-notes/promote',
    { note_id: noteId, subscriber_id: subscriberId, section },
  );
  if (out.kind === 'ok') return { promoted_to_context_at: out.data.promoted_to_context_at };
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  const ts = new Date().toISOString();
  const { error } = await supabase
    .from('your_notes')
    .update({ promoted_to_context_at: ts, promoted_section: section })
    .eq('id', noteId)
    .eq('subscriber_id', subscriberId);
  if (error) throw error;
  return { promoted_to_context_at: ts };
}

/** Accept a revision request. */
export async function acceptRevisionRequest(revisionId: string): Promise<void> {
  const out = await callOperatorEndpoint('/api/v1/operator/revisions/accept', {
    revision_id: revisionId,
  });
  if (out.kind === 'ok') return;
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  const { error } = await supabase
    .from('revision_requests')
    .update({ status: 'accepted', resolved_at: new Date().toISOString() })
    .eq('id', revisionId);
  if (error) throw error;
}

/** Decline a revision request. */
export async function declineRevisionRequest(revisionId: string): Promise<void> {
  const out = await callOperatorEndpoint('/api/v1/operator/revisions/decline', {
    revision_id: revisionId,
  });
  if (out.kind === 'ok') return;
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  const { error } = await supabase
    .from('revision_requests')
    .update({ status: 'declined', resolved_at: new Date().toISOString() })
    .eq('id', revisionId);
  if (error) throw error;
}

/** Re-dispatch the welcome moment for an already-reviewed onboarding row. */
export async function sendWelcomeForOnboarding(
  onboardingStateId: string,
  subscriberId: string,
): Promise<{ notification_dispatched: boolean }> {
  const out = await callOperatorEndpoint<{ notification_dispatched: boolean }>(
    '/api/v1/operator/onboarding/send-welcome',
    {
      onboarding_state_id: onboardingStateId,
      subscriber_id: subscriberId,
    },
  );
  if (out.kind === 'ok') {
    return {
      notification_dispatched: Boolean(out.data.notification_dispatched),
    };
  }
  if (out.kind === 'error') throw new Error(out.message);
  // No sandbox fallback for the re-dispatch path (the value is the
  // notification insert; we surface absent honestly).
  return { notification_dispatched: false };
}

/**
 * Send a delivered task to the subscriber. Endpoint-preferred with a
 * sandbox-only direct-supabase fallback. Returns the delivered_at timestamp
 * on success.
 */
export async function sendDeliveryForTask(
  taskId: string,
): Promise<{ delivered_at: string; notification_dispatched: boolean }> {
  const out = await callOperatorEndpoint<{
    delivered_at: string;
    notification_dispatched: boolean;
  }>('/api/v1/operator/deliveries/send', { task_id: taskId });
  if (out.kind === 'ok') {
    return {
      delivered_at: out.data.delivered_at,
      notification_dispatched: Boolean(out.data.notification_dispatched),
    };
  }
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only): operator RLS permits status + delivered_at write.
  const ts = new Date().toISOString();
  const { error: tErr } = await supabase
    .from('tasks')
    .update({ status: 'delivered', delivered_at: ts })
    .eq('id', taskId);
  if (tErr) throw tErr;
  return { delivered_at: ts, notification_dispatched: false };
}

/**
 * Send a reviewed progression-report draft to the subscriber. Endpoint-
 * preferred with a sandbox-only direct-supabase fallback. The AI-06
 * precondition (reviewed_by_eric_at set) is enforced server-side in the
 * shared service; the fallback enforces the same invariant client-side.
 */
export async function sendProgressionReport(id: string): Promise<{ delivered_at: string }> {
  const out = await callOperatorEndpoint<{ delivered_at: string }>(
    '/api/v1/operator/progression-reports/send',
    { report_id: id },
  );
  if (out.kind === 'ok') return { delivered_at: out.data.delivered_at };
  if (out.kind === 'error') throw new Error(out.message);
  // Fallback (sandbox only).
  const { data: existing, error: readErr } = await supabase
    .from('progression_reports')
    .select('reviewed_by_eric_at, delivered_at, subscriber_id')
    .eq('id', id)
    .single();
  if (readErr) throw readErr;
  if (!existing) throw new Error('Report not found.');
  if (existing.reviewed_by_eric_at === null) {
    throw new Error(
      'Mark the report reviewed before sending (AI-06 review gate).',
    );
  }
  if (existing.delivered_at !== null) {
    throw new Error('Report is already delivered.');
  }
  const deliveredAt = new Date().toISOString();
  const { error: updErr } = await supabase
    .from('progression_reports')
    .update({ delivered_at: deliveredAt })
    .eq('id', id);
  if (updErr) throw updErr;
  const { data: sub } = await supabase
    .from('subscribers')
    .select('user_id')
    .eq('id', (existing as { subscriber_id: string }).subscriber_id)
    .single();
  if (sub) {
    await supabase.from('notifications').insert({
      recipient_user_id: (sub as { user_id: string }).user_id,
      subscriber_id: (existing as { subscriber_id: string }).subscriber_id,
      type: 'report_ready',
      payload: { report_id: id },
    });
  }
  return { delivered_at: deliveredAt };
}

// ---- OS Foundation Batch 2 (PRD-004 §4 / §7) ----------------------------
//
// Operator reads + actions for the Operating System surface. Reads go through
// the os_*_operator_all RLS policies (migration 0027); the operator JWT
// app_metadata claim `role=operator` (T-016) is what permits them. The two
// state-changing actions wrap the provision_os_owner() / advance_os_status()
// SECURITY DEFINER RPCs through the typed POST endpoints at /api/v1/operator/os/*
// (ARCH-004 §6 single source: the same RPC the Studio MCP tools call). No
// sandbox fallback for these two: the privileged write lives inside the RPC
// (ARCH-013) and there is no operator-RLS direct INSERT to fall back to.

/** os_leads row for the operator review queue (DATA-OS-02). */
export interface OsLeadRow {
  id: string;
  email: string;
  name: string | null;
  painful_workflow: string;
  responses: Record<string, unknown>;
  status: 'new' | 'reviewing' | 'accepted' | 'declined' | 'provisioned';
  reviewed_at: string | null;
  provisioned_engagement_id: string | null;
  created_at: string;
}

const OS_LEAD_COLS =
  'id, email, name, painful_workflow, responses, status, reviewed_at, provisioned_engagement_id, created_at';

/**
 * All OS leads, newest first. Capped at 200 (single operator volume). Source
 * for the /eric/os leads panel.
 */
export async function listOsLeads(): Promise<OsLeadRow[]> {
  const { data, error } = await supabase
    .from('os_leads')
    .select(OS_LEAD_COLS)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as OsLeadRow[];
}

export type OsFundingType =
  | 'comped_guest'
  | 'direct_pay'
  | 'included_in_subscription';

export type OsPaymentState = 'comped' | 'awaiting_payment' | 'confirmed';

export type OsStatus =
  | 'intake'
  | 'acknowledged'
  | 'scoped'
  | 'session1_booked'
  | 'discovery_done'
  | 'building'
  | 'handoff_ready'
  | 'session2_booked'
  | 'installed'
  | 'open';

/** os_engagements row joined with the owner's email (DATA-OS-01). */
export interface OsEngagementRow {
  id: string;
  owner_subscriber_id: string;
  lead_id: string | null;
  funding_type: OsFundingType;
  payment_state: OsPaymentState;
  status: OsStatus;
  scope: string | null;
  entry_workflow: string | null;
  ready_by: string | null;
  price_amount: number | null;
  price_currency: 'NOK' | 'EUR' | null;
  acknowledged_at: string | null;
  scoped_at: string | null;
  session1_booked_at: string | null;
  discovery_done_at: string | null;
  handoff_ready_at: string | null;
  session2_booked_at: string | null;
  installed_at: string | null;
  opened_at: string | null;
  session1_booking_url: string | null;
  session2_booking_url: string | null;
  created_at: string;
  owner: { id: string; email: string } | null;
}

const OS_ENGAGEMENT_COLS =
  'id, owner_subscriber_id, lead_id, funding_type, payment_state, status, scope, entry_workflow, ready_by, price_amount, price_currency, acknowledged_at, scoped_at, session1_booked_at, discovery_done_at, handoff_ready_at, session2_booked_at, installed_at, opened_at, session1_booking_url, session2_booking_url, created_at, owner:subscribers!os_engagements_owner_subscriber_id_fkey(id, email)';

/**
 * All non-deleted OS engagements, newest first. Capped at 200. Source for the
 * /eric/os engagements panel + the per-engagement lifecycle controls.
 */
export async function listOsEngagements(): Promise<OsEngagementRow[]> {
  const { data, error } = await supabase
    .from('os_engagements')
    .select(OS_ENGAGEMENT_COLS)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as OsEngagementRow[];
}

/**
 * Set the out-of-band booking URLs on an engagement (migration 0032). The owner
 * surface renders the "Book session one" CTA (at the scoped stage) and the
 * "Book the install" CTA (at handoff_ready) only when the matching URL is set.
 *
 * This is a DIRECT operator write under os_engagements_operator_all (migration
 * 0007), the same posture as the other operator direct-write helpers (e.g.
 * setTaskStatus). It deliberately does NOT touch the advance_os_status() RPC
 * (migration 0029 is frozen): these two columns are operator scratch, not part
 * of the status-advance transition. Passing `null` clears a URL (hides the CTA);
 * passing a non-empty string sets it. An empty/whitespace string is normalized
 * to null so a blank input clears rather than stores a broken link.
 */
export async function setOsBookingUrls(
  engagementId: string,
  urls: { session1_booking_url?: string | null; session2_booking_url?: string | null },
): Promise<void> {
  const patch: Record<string, string | null> = {};
  if (urls.session1_booking_url !== undefined) {
    const v = urls.session1_booking_url?.trim();
    patch.session1_booking_url = v && v.length > 0 ? v : null;
  }
  if (urls.session2_booking_url !== undefined) {
    const v = urls.session2_booking_url?.trim();
    patch.session2_booking_url = v && v.length > 0 ? v : null;
  }
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase
    .from('os_engagements')
    .update(patch)
    .eq('id', engagementId);
  if (error) throw error;
}

/**
 * Provision an OS owner + engagement via the operator-action endpoint. Wraps
 * provision_os_owner() (TECH-OS-03). Returns the new engagement id. Omitted
 * optional fields are dropped from the body so the RPC defaults apply.
 */
export interface ProvisionOsOwnerArgs {
  owner_email: string;
  funding_type: OsFundingType;
  subscriber_id?: string;
  lead_id?: string;
  payment_confirmed?: boolean;
  scope?: string;
  entry_workflow?: string;
  price_amount?: number;
  price_currency?: 'NOK' | 'EUR';
}

export async function provisionOsOwner(
  args: ProvisionOsOwnerArgs,
): Promise<{ engagement_id: string }> {
  const body: Record<string, unknown> = {
    owner_email: args.owner_email,
    funding_type: args.funding_type,
  };
  if (args.subscriber_id !== undefined) body.subscriber_id = args.subscriber_id;
  if (args.lead_id !== undefined) body.lead_id = args.lead_id;
  if (args.payment_confirmed !== undefined) {
    body.payment_confirmed = args.payment_confirmed;
  }
  if (args.scope !== undefined) body.scope = args.scope;
  if (args.entry_workflow !== undefined) body.entry_workflow = args.entry_workflow;
  if (args.price_amount !== undefined) body.price_amount = args.price_amount;
  if (args.price_currency !== undefined) body.price_currency = args.price_currency;

  const out = await callOperatorEndpoint<{ engagement_id: string }>(
    '/api/v1/operator/os/provision',
    body,
  );
  if (out.kind === 'ok') return { engagement_id: out.data.engagement_id };
  if (out.kind === 'error') throw new Error(out.message);
  // No sandbox fallback: the privileged write lives inside the SECURITY
  // DEFINER RPC (ARCH-013); there is no operator-RLS direct INSERT path.
  throw new Error(
    'OS provisioning endpoint is not available in this environment.',
  );
}

/**
 * Advance an OS engagement's lifecycle state via the operator-action
 * endpoint. Wraps advance_os_status() (PR-OS-01 / TECH-OS-04). Returns the new
 * os_status. `ready_by` is a YYYY-MM-DD date; `booked_at` is an ISO timestamp
 * for the two booking beats.
 */
export interface AdvanceOsStatusArgs {
  engagement_id: string;
  target_status: OsStatus;
  scope?: string;
  entry_workflow?: string;
  ready_by?: string;
  booked_at?: string;
}

export async function advanceOsStatus(
  args: AdvanceOsStatusArgs,
): Promise<{ status: OsStatus }> {
  const body: Record<string, unknown> = {
    engagement_id: args.engagement_id,
    target_status: args.target_status,
  };
  if (args.scope !== undefined) body.scope = args.scope;
  if (args.entry_workflow !== undefined) body.entry_workflow = args.entry_workflow;
  if (args.ready_by !== undefined) body.ready_by = args.ready_by;
  if (args.booked_at !== undefined) body.booked_at = args.booked_at;

  const out = await callOperatorEndpoint<{ status: OsStatus }>(
    '/api/v1/operator/os/advance-status',
    body,
  );
  if (out.kind === 'ok') return { status: out.data.status };
  if (out.kind === 'error') throw new Error(out.message);
  // No sandbox fallback (see provisionOsOwner).
  throw new Error(
    'OS status-advance endpoint is not available in this environment.',
  );
}

// ---- OS handoff composer (Batch 3, PRD-004 scoping pass §6 TECH-OS-01) ---
//
// The handoff package is authored as a real deliverables row with task_id NULL
// (migration 0031), with its downloadable artifacts uploaded to the private
// 'os-handoffs' storage bucket and recorded in os_handoff_files. All three
// writes run under operator RLS (deliverables_operator_all migration 0007,
// os_handoffs_operator_all migration 0030, os_handoff_files_operator_all
// migration 0027) - no new SECURITY DEFINER RPC is needed because none of these
// is a privileged self-provision; the operator JWT claim (T-016) is sufficient.
//
// Sequence (honest, no silent partials): (1) INSERT the deliverable and capture
// its id, (2) for each file upload the bytes then INSERT the metadata row, (3)
// point os_engagements.handoff_deliverable_id at the new deliverable. If a file
// upload or metadata insert fails mid-way, we throw immediately with the
// failing filename so the operator sees a partial handoff rather than a silent
// success. (V1 has no rollback of the rows already written; the operator can
// re-run after fixing the bad file - re-running re-inserts a fresh deliverable.
// A transactional all-or-nothing composer is a documented follow-up.)

export interface OsHandoffFileInput {
  file: File;
  label?: string;
}

export interface ComposeOsHandoffArgs {
  engagementId: string;
  readmeMd: string;
  mentalModel: string;
  videoUrl?: string | null;
  videoProvider?: VideoProvider | null;
  files: OsHandoffFileInput[];
}

export async function composeOsHandoff(
  args: ComposeOsHandoffArgs,
): Promise<{ deliverableId: string }> {
  const readme = args.readmeMd.trim();
  const mentalModel = args.mentalModel.trim();
  if (readme.length === 0) {
    throw new Error('The README cannot be empty.');
  }
  if (mentalModel.length === 0) {
    throw new Error('The mental model line cannot be empty.');
  }

  const videoUrl =
    args.videoUrl && args.videoUrl.trim().length > 0
      ? args.videoUrl.trim()
      : null;
  const videoProvider = videoUrl ? (args.videoProvider ?? null) : null;

  // 1. INSERT the handoff deliverable (task_id NULL, os_handoff content).
  const { data: inserted, error: dErr } = await supabase
    .from('deliverables')
    .insert({
      task_id: null,
      content: {
        kind: 'os_handoff',
        readme_md: readme,
        mental_model: mentalModel,
      },
      content_schema_version: JSONB_SCHEMA_VERSIONS.deliverableContent,
      video_url: videoUrl,
      video_provider: videoProvider,
    })
    .select('id')
    .single();
  if (dErr) throw dErr;
  const deliverableId = (inserted as { id: string }).id;

  // 2. Upload each file, then record its metadata row. Key is
  // '<deliverableId>/<filename>' (the storage_path = storage.objects.name
  // convention the owner-read RLS keys on, migration 0030).
  for (let i = 0; i < args.files.length; i += 1) {
    const entry = args.files[i];
    if (!entry) continue;
    const { file, label } = entry;
    const storagePath = `${deliverableId}/${file.name}`;
    const { error: upErr } = await supabase.storage
      .from('os-handoffs')
      .upload(storagePath, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: true,
      });
    if (upErr) {
      throw new Error(
        `Uploaded ${i} of ${args.files.length} files, then failed on "${file.name}": ${upErr.message}`,
      );
    }
    const { error: fErr } = await supabase.from('os_handoff_files').insert({
      deliverable_id: deliverableId,
      filename: file.name,
      storage_path: storagePath,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      label: label && label.trim().length > 0 ? label.trim() : null,
      sort_order: i,
    });
    if (fErr) {
      throw new Error(
        `Uploaded "${file.name}" but could not record its metadata row: ${fErr.message}`,
      );
    }
  }

  // 3. Point the engagement at the new handoff deliverable.
  const { error: eErr } = await supabase
    .from('os_engagements')
    .update({ handoff_deliverable_id: deliverableId })
    .eq('id', args.engagementId);
  if (eErr) {
    throw new Error(
      `Built the handoff deliverable (${deliverableId}) but could not attach it to the engagement: ${eErr.message}`,
    );
  }

  return { deliverableId };
}

// ---- Notification counter for the top bar -------------------------------

export async function countOperatorUnread(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('recipient_user_id', userId)
    .is('read_at', null);
  if (error) {
    return 0;
  }
  return count ?? 0;
}
